import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {
  TextField,
  Tooltip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExtensionIcon from "@mui/icons-material/Extension";
import InstagramIcon from "@mui/icons-material/Instagram";
import ForumIcon from "@mui/icons-material/Forum";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StorageIcon from "@mui/icons-material/Storage";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import FlagIcon from "@mui/icons-material/Flag";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PublicIcon from "@mui/icons-material/Public";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { useTheme } from "../../../contexts/ThemeContext";

// Renders any node type without a dedicated component, using the presentation
// metadata the backend attaches to each graph node (workflow/node_details.py).
export const GENERIC_NODE_TYPE = "GENERIC";

// Backend `icon` names -> MUI icons
const ICONS = {
  instagram: InstagramIcon,
  "message-circle": ForumIcon,
  bot: SmartToyIcon,
  database: StorageIcon,
  "message-square": WhatsAppIcon,
  smartphone: SmsIcon,
  mail: EmailIcon,
  "phone-outgoing": PhoneForwardedIcon,
  "git-fork": CallSplitIcon,
  split: AltRouteIcon,
  target: FlagIcon,
  hourglass: HourglassEmptyIcon,
  "clock-pause": PauseCircleOutlineIcon,
  variable: DataObjectIcon,
  "user-pen": PersonOutlineIcon,
  tags: LocalOfferIcon,
  "clipboard-list": AssignmentIcon,
  globe: PublicIcon,
};

const JSON_FIELD_TYPES = new Set(["array", "keyValue"]);

const humanize = (key) =>
  String(key)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Declared branches first, then any branch already wired in transitions/edges,
// so every existing edge has a matching source handle to attach to.
const getBranches = (data, nodeId, edges) => {
  const branches = (data?.supportedTransitions || []).map((t) => ({
    key: t.branch,
    label: t.label || humanize(t.branch),
  }));
  const wiredKeys = [
    ...Object.keys(data?.transitions || {}),
    ...edges.filter((e) => e.source === nodeId).map((e) => e.sourceHandle || "next"),
  ];
  wiredKeys.forEach((key) => {
    if (!branches.some((b) => b.key === key)) {
      branches.push({ key, label: humanize(key) });
    }
  });
  if (!branches.length) branches.push({ key: "next", label: "Next" });
  return branches;
};

// First filled text field of the config (e.g. the message), shown when the
// backend summary is missing or was cleared by an edit.
const firstTextValue = (data) => {
  const cfg = data?.config || {};
  const field = (data?.fieldsSchema || []).find(
    (f) => ["textarea", "text"].includes(f.type) && typeof cfg[f.name] === "string" && cfg[f.name].trim()
  );
  return field ? cfg[field.name] : null;
};

const toFormValue = (field, value) => {
  if (JSON_FIELD_TYPES.has(field.type)) {
    return value === undefined ? "" : JSON.stringify(value, null, 2);
  }
  if (field.type === "boolean") return !!value;
  return value ?? "";
};

export const GenericNodeEditModal = ({ open, handleClose, data }) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;
  const nodeData = data?.data || {};
  const fields = nodeData.fieldsSchema || [];

  const [label, setLabel] = useState("");
  const [values, setValues] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const cfg = data?.data?.config || {};
    const initial = {};
    (data?.data?.fieldsSchema || []).forEach((f) => {
      initial[f.name] = toFormValue(f, cfg[f.name]);
    });
    setLabel(data?.data?.label || "");
    setValues(initial);
    setError("");
  }, [open, data]);

  const setValue = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const missingRequired = fields.some(
    (f) => f.required && f.type !== "boolean" && String(values[f.name] ?? "").trim() === ""
  );

  const onSave = () => {
    const config = {};
    for (const f of fields) {
      const raw = values[f.name];
      if (JSON_FIELD_TYPES.has(f.type)) {
        if (!String(raw || "").trim()) continue;
        try {
          config[f.name] = JSON.parse(raw);
        } catch (e) {
          setError(`${f.label || f.name} must be valid JSON`);
          return;
        }
      } else if (f.type === "number") {
        if (raw !== "") config[f.name] = Number(raw);
      } else {
        config[f.name] = raw;
      }
    }
    handleClose({ label: label.trim() || nodeData.displayName, config }, isEdit);
  };

  const renderField = (f) => {
    const value = values[f.name];
    const common = {
      key: f.name,
      fullWidth: true,
      size: "small",
      label: `${f.label || humanize(f.name)}${f.required ? " *" : ""}`,
      placeholder: f.placeholder,
      helperText: f.helpText,
    };

    if (f.type === "boolean") {
      return (
        <div key={f.name}>
          <FormControlLabel
            control={<Switch checked={!!value} onChange={(e) => setValue(f.name, e.target.checked)} />}
            label={f.label || humanize(f.name)}
          />
          {f.helpText && (
            <div className={`text-[11px] ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>{f.helpText}</div>
          )}
        </div>
      );
    }

    if (f.type === "select" && Array.isArray(f.options) && f.options.length) {
      return (
        <FormControl key={f.name} fullWidth size="small">
          <InputLabel id={`generic-${f.name}-label`}>{common.label}</InputLabel>
          <Select
            labelId={`generic-${f.name}-label`}
            value={value}
            label={common.label}
            onChange={(e) => setValue(f.name, e.target.value)}
          >
            {f.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {f.helpText && (
            <div className={`text-[11px] mt-1 ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>{f.helpText}</div>
          )}
        </FormControl>
      );
    }

    if (f.type === "textarea" || JSON_FIELD_TYPES.has(f.type)) {
      return (
        <TextField
          {...common}
          multiline
          rows={JSON_FIELD_TYPES.has(f.type) ? 5 : 3}
          value={value}
          onChange={(e) => setValue(f.name, e.target.value)}
          helperText={JSON_FIELD_TYPES.has(f.type) ? `${f.helpText || ""} (JSON)`.trim() : f.helpText}
          inputProps={JSON_FIELD_TYPES.has(f.type) ? { style: { fontFamily: "monospace", fontSize: 12 } } : undefined}
        />
      );
    }

    return (
      <TextField
        {...common}
        type={f.type === "number" ? "number" : "text"}
        value={value}
        onChange={(e) => setValue(f.name, e.target.value)}
      />
    );
  };

  const Icon = ICONS[nodeData.icon] || ExtensionIcon;
  const color = nodeData.color || "#64748B";

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={() => handleClose(null)}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}1a`, color }}>
            <Icon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              {isEdit ? "Edit" : "Configure"} {nodeData.displayName || humanize(nodeData.nodeType || "Node")}
            </h2>
            {nodeData.description && (
              <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>{nodeData.description}</p>
            )}
          </div>
        </div>

        <div className="space-y-4 my-4">
          <TextField
            fullWidth
            size="small"
            label="Step Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          {fields.length ? (
            fields.map(renderField)
          ) : (
            <div className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              This step has no configurable fields.
            </div>
          )}
          {error && <div className="text-xs font-medium text-rose-500">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={() => handleClose(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={missingRequired}
            style={{ backgroundColor: missingRequired ? undefined : color }}
          >
            {isEdit ? "Update Node" : "Add Node"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export const GenericNode = (props) => {
  const { data, selected, onEdit, onAdd, onDeleteNode, edges = [], ...rest } = props;
  const { darkMode } = useTheme();

  const Icon = ICONS[data?.icon] || ExtensionIcon;
  const color = data?.color || "#64748B";
  const title = data?.displayName || humanize(data?.nodeType || "Step");
  const label = data?.label && data.label !== title ? data.label : null;
  const summaryText = data?.summary || firstTextValue(data);
  const summary = summaryText && summaryText !== data?.label ? summaryText : null;
  const branches = getBranches(data, rest.id, edges);

  const isBranchConnected = (branchKey) =>
    edges.some(
      (e) => e.source === rest.id && (e.sourceHandle === branchKey || (!e.sourceHandle && branchKey === "next"))
    );

  return (
    <div
      className={`w-64 shadow-lg rounded-xl overflow-hidden transition-all duration-200 ${
        darkMode ? "bg-[#18181b] border border-zinc-700" : "bg-white border border-slate-200"
      }`}
      style={selected ? { boxShadow: `0 0 0 2px ${color}` } : undefined}
    >
      <Handle id="target" type="target" position={Position.Left} />

      <div
        className="flex items-center justify-between px-3 py-2 text-white"
        style={{ background: `linear-gradient(to right, ${color}, ${color}cc)` }}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon style={{ fontSize: 16 }} />
          <span className="text-xs font-semibold tracking-wide uppercase truncate">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit Node">
            <EditIcon style={{ fontSize: 14, cursor: "pointer" }} onClick={() => onEdit(props)} />
          </Tooltip>
          {onDeleteNode && rest.id !== "1" && (
            <Tooltip title="Delete Node">
              <DeleteOutlineIcon
                style={{ fontSize: 15, cursor: "pointer" }}
                onClick={() => onDeleteNode(rest.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-2.5">
        {label && (
          <div className={`text-xs font-medium ${darkMode ? "text-zinc-100" : "text-slate-800"}`}>{label}</div>
        )}
        {summary && (
          <div className={`text-[11px] mt-1 line-clamp-2 ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
            {summary}
          </div>
        )}
        {data?.badge && (
          <span
            className="inline-block text-[10px] font-semibold mt-1.5 px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${color}1a`, color }}
          >
            {data.badge}
          </span>
        )}
      </div>

      <div
        className={`border-t ${
          darkMode ? "border-zinc-800 bg-zinc-900/50" : "border-slate-100 bg-slate-50/50"
        } px-2.5 py-2 space-y-1.5`}
      >
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400 mb-1">Outcomes</div>
        {branches.map((b) => (
          <div key={b.key} className="flex items-center justify-between relative py-0.5">
            <span className={`text-[11px] font-medium ${darkMode ? "text-zinc-300" : "text-slate-600"}`}>
              {b.label}
            </span>
            <HandleDot
              addVisible={!isBranchConnected(b.key)}
              id={b.key}
              type="source"
              position={Position.Right}
              onAdd={(v) =>
                onAdd(v, rest, rest.id, {
                  type: data?.nodeType,
                  key: b.key,
                })
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};
